import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { User, Lock, MapPin, Calendar, DollarSign, AlertCircle, Users, Briefcase, FileText, X, CheckCircle, Mail, Shield } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import IndianPhoneInput from '../components/IndianPhoneInput';
import '../components/IndianPhoneInput.css';
import axios from 'axios';
import useSeo from '../hooks/useSeo';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COUNTDOWN_SECONDS = 60;

/** Extract 10-digit Indian number from E.164 value (+919876543210 → 9876543210) */
const getCleanPhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length >= 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length === 10) return digits;
  return digits;
};

const CASTE_CATEGORIES = ['General', 'SC (Scheduled Caste)', 'ST (Scheduled Tribe)', 'OBC (Other Backward Class)', 'Minority'];

const OCCUPATION_OPTIONS = [
  { key: 'Agriculture', translationKey: 'occupationAgriculture' },
  { key: 'IT', translationKey: 'occupationIT' },
  { key: 'Banking', translationKey: 'occupationBanking' },
  { key: 'Business', translationKey: 'occupationBusiness' },
  { key: 'Accountant', translationKey: 'occupationAccountant' },
  { key: 'Home Maker', translationKey: 'occupationHomeMaker' },
  { key: 'Government or Civil Services', translationKey: 'occupationGovernment' },
  { key: 'Health Care', translationKey: 'occupationHealthCare' },
  { key: 'Traditional Crafts or Handicraft', translationKey: 'occupationCrafts' },
  { key: 'Manual Labor', translationKey: 'occupationManualLabor' },
  { key: 'Creative Media', translationKey: 'occupationCreativeMedia' },
  { key: 'Student', translationKey: 'occupationStudent' },
  { key: 'Sales or Retail Workers', translationKey: 'occupationSalesRetail' },
  { key: 'Teachers', translationKey: 'occupationTeachers' }
];

const INCOME_RANGES = [
  'Below ₹50,000', '₹50,000 - ₹1 lakh', '₹1 lakh - ₹2 lakhs', '₹2 lakhs - ₹3 lakhs',
  '₹3 lakhs - ₹5 lakhs', '₹5 lakhs - ₹8 lakhs', '₹8 lakhs - ₹10 lakhs',
  '₹10 lakhs - ₹15 lakhs', '₹15 lakhs - ₹20 lakhs', 'Above ₹20 lakhs'
];

const DISTRICTS = {
  'Andhra Pradesh': ['Alluri Sitharama Raju', 'Anakapalli', 'Anantapur', 'Annamayya', 'Bapatla', 'Chittoor', 'East Godavari', 'Eluru', 'Guntur', 'Kakinada', 'Konaseema (Dr. B.R. Ambedkar)', 'Krishna', 'Kurnool', 'Madanapalle', 'Markapuram', 'Nandyal', 'Nellore (SPSR)', 'NTR District', 'Palnadu', 'Parvathipuram Manyam', 'Polavaram', 'Prakasam', 'Sri Sathya Sai', 'Srikakulam', 'Tirupati', 'Visakhapatnam', 'Vizianagaram', 'West Godavari', 'YSR Kadapa'],
  'Telangana': ['Adilabad', 'Bhadradri Kothagudem', 'Hanamkonda (formerly Warangal Urban)', 'Hyderabad', 'Jagtial', 'Jangaon', 'Jayashankar Bhupalpally', 'Jogulamba Gadwal', 'Kamareddy', 'Karimnagar', 'Khammam', 'Kumuram Bheem Asifabad', 'Mahabubabad', 'Mahabubnagar', 'Mancherial', 'Medak', 'Medchal–Malkajgiri', 'Mulugu', 'Nagarkurnool', 'Nalgonda', 'Narayanpet', 'Nirmal', 'Nizamabad', 'Peddapalli', 'Rajanna Sircilla', 'Rangareddy', 'Sangareddy', 'Siddipet', 'Suryapet', 'Vikarabad', 'Wanaparthy', 'Warangal (formerly Warangal Rural)', 'Yadadri Bhuvanagiri'],
  'Karnataka': ['Bagalkote', 'Ballari (Bellary)', 'Belagavi (Belgaum)', 'Bengaluru Rural', 'Bengaluru Urban', 'Bidar', 'Chamarajanagar', 'Chikkaballapur', 'Chikkamagaluru (Chikmagalur)', 'Chitradurga', 'Dakshina Kannada', 'Davanagere', 'Dharwad', 'Gadag', 'Hassan', 'Haveri', 'Kalaburagi (Gulbarga)', 'Kodagu (Coorg)', 'Kolar', 'Koppal', 'Mandya', 'Mysuru (Mysore)', 'Raichur', 'Ramanagara', 'Shivamogga (Shimoga)', 'Tumakuru (Tumkur)', 'Udupi', 'Uttara Kannada (Karwar)', 'Vijayapura (Bijapur)', 'Vijayanagara (Newest)', 'Yadgir']
};

const Signup = () => {
  useSeo({
    title: 'Sign Up — KnowMySchemes',
    description: 'Create a free KnowMySchemes account to save schemes and jobs, run unlimited KMS Smart analyses, and get personalised recommendations.',
    canonical: 'https://knowmyschemes.com/signup',
    noindex: true,
  });
  const navigate = useNavigate();
  const { signupWithOTP } = useAuth();
  const { t, language } = useLanguage();

  const [formData, setFormData] = useState({
    name: '', phone: '', password: '', confirmPassword: '', email: '',
    state: '', district: '', age: '', caste: '', occupation: '', household_income: ''
  });
  const [error, setError] = useState('');
  const [duplicatePhone, setDuplicatePhone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [translatedTerms, setTranslatedTerms] = useState(null);
  const termsRef = React.useRef(null);

  // Email OTP state
  const [emailOtp, setEmailOtp] = useState('');

  // Translate terms content when modal opens and language is not English
  const translateTermsContent = useCallback(async () => {
    if (language === 'en' || !showTermsModal || !termsRef.current) {
      setTranslatedTerms(null);
      return;
    }
    try {
      const el = termsRef.current;
      const textNodes = [];
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
      let node;
      while ((node = walker.nextNode())) {
        const text = node.textContent.trim();
        if (text.length > 1) textNodes.push(text);
      }
      if (textNodes.length === 0) return;
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const batchSize = 50;
      const allTranslated = [];
      for (let i = 0; i < textNodes.length; i += batchSize) {
        const batch = textNodes.slice(i, i + batchSize);
        const res = await fetch(`${API_URL}/api/translate/batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ texts: batch, target_language: language })
        });
        const data = await res.json();
        const translations = (data.translations || []).map(t => typeof t === 'string' ? t : t.translated || t.original || '');
        allTranslated.push(...(translations.length ? translations : batch));
      }
      // Build a map of original -> translated
      const map = {};
      textNodes.forEach((t, i) => { map[t] = allTranslated[i] || t; });
      setTranslatedTerms(map);
    } catch (err) {
      console.error('Terms translation error:', err);
    }
  }, [language, showTermsModal]);

  useEffect(() => {
    if (showTermsModal) {
      // Small delay to let DOM render first
      const timer = setTimeout(translateTermsContent, 300);
      return () => clearTimeout(timer);
    }
  }, [showTermsModal, language, translateTermsContent]);

  // Apply translations to DOM when translatedTerms changes
  useEffect(() => {
    if (!translatedTerms || !termsRef.current || language === 'en') return;
    const el = termsRef.current;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    let node;
    while ((node = walker.nextNode())) {
      const text = node.textContent.trim();
      if (text.length > 1 && translatedTerms[text]) {
        node.textContent = translatedTerms[text];
      }
    }
  }, [translatedTerms, language]);
  const [emailVerificationToken, setEmailVerificationToken] = useState('');
  const [emailCountdown, setEmailCountdown] = useState(0);
  const emailTimerRef = useRef(null);
  const emailOtpInputRef = useRef(null);

  // Phone OTP state
  const [step, setStep] = useState('form'); // 'form' | 'email-otp' | 'otp'
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [reqId, setReqId] = useState('');
  const [otpSentAt, setOtpSentAt] = useState(null);
  const timerRef = useRef(null);
  const otpInputRef = useRef(null);

  // Phone OTP Countdown
  useEffect(() => {
    if (countdown <= 0) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => {
      setCountdown(prev => { if (prev <= 1) { clearInterval(timerRef.current); return 0; } return prev - 1; });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [countdown]);

  // Email OTP Countdown
  useEffect(() => {
    if (emailCountdown <= 0) { clearInterval(emailTimerRef.current); return; }
    emailTimerRef.current = setInterval(() => {
      setEmailCountdown(prev => { if (prev <= 1) { clearInterval(emailTimerRef.current); return 0; } return prev - 1; });
    }, 1000);
    return () => clearInterval(emailTimerRef.current);
  }, [emailCountdown]);

  useEffect(() => {
    if (step === 'otp' && otpInputRef.current) otpInputRef.current.focus();
    if (step === 'email-otp' && emailOtpInputRef.current) emailOtpInputRef.current.focus();
  }, [step]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'state') setFormData({ ...formData, state: value, district: '' });
    else setFormData({ ...formData, [name]: value });
    setError('');
  };

  // Send email OTP for signup verification
  const sendEmailOTP = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/email/send-signup-otp`, { email: formData.email });
      setEmailCountdown(COUNTDOWN_SECONDS);
      setEmailOtp('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send email OTP');
      setLoading(false);
      return false;
    }
    setLoading(false);
    return true;
  };

  // Verify email OTP
  const handleVerifyEmailOTP = async () => {
    setError('');
    if (emailOtp.length < 6) { setError('Please enter the 6-digit OTP sent to your email'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/email/verify-signup-otp`, {
        email: formData.email,
        otp: emailOtp,
      });
      setEmailVerificationToken(res.data.email_verification_token);
      setLoading(false);
      // Email verified, now proceed to phone OTP
      sendPhoneOTP();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired OTP');
      setLoading(false);
    }
  };

  // Send phone OTP via MSG91
  const sendPhoneOTP = () => {
    const cleanedPhone = getCleanPhone(formData.phone);
    if (typeof window.sendOtp !== 'function') {
      setError('OTP service is loading. Please refresh the page and try again.');
      return;
    }
    setLoading(true);
    window.sendOtp(
      `91${cleanedPhone}`,
      (data) => {
        setOtpSentAt(Date.now());
        setStep('otp');
        setCountdown(COUNTDOWN_SECONDS);
        setOtp('');
        setLoading(false);
      },
      (error) => {
        console.error('MSG91 sendOtp error:', error);
        let msg;
        if (typeof error === 'string') msg = error;
        else if (Array.isArray(error)) msg = error.join(', ');
        else if (error?.message) msg = error.message;
        else msg = JSON.stringify(error);
        setError(msg || 'Failed to send OTP');
        setLoading(false);
      }
    );
  };

  // Validate form -> send email OTP first
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.phone || !formData.password || !formData.confirmPassword || !formData.email || !formData.state || !formData.district || !formData.age || !formData.caste || !formData.occupation || !formData.household_income) {
      setError('Please fill in all required fields'); return;
    }
    if (!termsAccepted) { setError(t('termsNotAccepted') || 'You must accept the Terms, Conditions, and Privacy Policy to register'); return; }
    if (formData.name.length < 2) { setError('Name must be at least 2 characters long'); return; }
    const cleanedPhone = getCleanPhone(formData.phone);
    if (cleanedPhone.length !== 10) { setError('Please enter a valid 10-digit phone number'); return; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters long'); return; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }

    if (typeof window.sendOtp !== 'function') {
      setError('OTP service is loading. Please refresh the page and try again.');
      return;
    }

    setLoading(true);
    setDuplicatePhone(false);

    // Check if phone is already registered before sending OTP
    try {
      const checkResponse = await axios.post(`${API_URL}/api/auth/check-phone`, { phone: cleanedPhone });
      if (!checkResponse.data.success) {
        setError('Unable to verify phone number. Please try again.');
        setLoading(false);
        return;
      }
    } catch (checkError) {
      if (checkError.response?.status === 409) {
        setError('This number is already registered.');
        setDuplicatePhone(true);
        setLoading(false);
        return;
      }
      console.error('Check phone error:', checkError);
    }

    // Send email OTP first
    const emailSent = await sendEmailOTP();
    if (emailSent) {
      setStep('email-otp');
    }
  };

  // Verify OTP via widget -> create account via backend
  const handleVerifyOTP = async () => {
    setError('');
    if (otp.length < 4) { setError('Please enter the OTP sent to your phone'); return; }

    if (typeof window.verifyOtp !== 'function') {
      setError('OTP service is loading. Please refresh the page and try again.');
      return;
    }

    // Enforce 5-minute expiry
    if (otpSentAt && (Date.now() - otpSentAt) > 5 * 60 * 1000) {
      setError('OTP has expired. Please request a new one.');
      return;
    }

    setLoading(true);

    window.verifyOtp(
      otp,
      async (data) => {
        const widgetToken = data?.accessToken || data?.access_token || data?.message || data?.token || (typeof data === 'string' ? data : JSON.stringify(data));
        const cleanedPhone = getCleanPhone(formData.phone);
        const result = await signupWithOTP({
          phone: cleanedPhone,
          password: formData.password,
          name: formData.name,
          district: `${formData.district}, ${formData.state}`,
          age: formData.age,
          caste: formData.caste,
          occupation: formData.occupation,
          household_income: formData.household_income,
          email: formData.email,
          email_verification_token: emailVerificationToken,
          terms_accepted: termsAccepted,
          access_token: widgetToken,
        });
        setLoading(false);

        if (result.success) {
          navigate('/');
        } else {
          setError(result.error);
        }
      },
      (error) => {
        console.error('MSG91 verifyOtp error:', error);
        let msg;
        if (typeof error === 'string') msg = error;
        else if (Array.isArray(error)) msg = error.join(', ');
        else if (error?.message) msg = error.message;
        else msg = JSON.stringify(error);
        setError(msg || 'Invalid or expired OTP');
        setLoading(false);
      }
    );
  };

  const handleResendOTP = async () => {
    setError('');
    if (typeof window.retryOtp !== 'function') {
      setError('OTP service is loading. Please refresh the page and try again.');
      return;
    }
    setLoading(true);

    window.retryOtp(
      "text",
      (data) => {
        setOtpSentAt(Date.now());
        setCountdown(COUNTDOWN_SECONDS);
        setOtp('');
        setLoading(false);
      },
      (error) => {
        console.error('MSG91 retryOtp error:', error);
        let msg;
        if (typeof error === 'string') msg = error;
        else if (Array.isArray(error)) msg = error.join(', ');
        else if (error?.message) msg = error.message;
        else msg = JSON.stringify(error);
        setError(msg || 'Failed to resend OTP');
        setLoading(false);
      }
    );
  };

  // ── Email OTP Verification Screen ──
  if (step === 'email-otp') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center px-3 py-6 sm:p-4 sm:py-12">
        <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
        <Card className="w-full max-w-md p-6 sm:p-8 shadow-2xl mx-1 sm:mx-0" data-testid="signup-email-otp-screen">
          <div className="flex justify-center mb-5">
            <img src="/logo.jpeg" alt="Logo" className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl shadow-lg object-cover" />
          </div>
          <div className="text-center mb-6">
            <Mail className="w-10 h-10 text-green-600 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-gray-900 mb-1">Verify Your Email</h2>
            <p className="text-sm text-gray-500">A 6-digit OTP has been sent to <strong>{formData.email}</strong></p>
            <button onClick={() => { setStep('form'); setEmailOtp(''); setError(''); }} className="text-xs text-green-600 hover:underline mt-1" data-testid="change-email-btn">Change email</button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="signup-email-otp-error">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Enter Email OTP</label>
              <input ref={emailOtpInputRef} type="text" value={emailOtp}
                onChange={(e) => { setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleVerifyEmailOTP(); } }}
                placeholder="● ● ● ● ● ●" maxLength={6}
                className="w-full px-4 py-3 text-center text-2xl tracking-[0.5em] font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                data-testid="signup-email-otp-input" />
            </div>

            <Button onClick={handleVerifyEmailOTP} disabled={loading || emailOtp.length < 6}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-3 text-base font-semibold"
              data-testid="signup-verify-email-otp-btn">
              {loading ? 'Verifying...' : 'Verify Email'}
            </Button>

            <div className="text-center">
              {emailCountdown > 0 ? (
                <p className="text-sm text-gray-500">Resend OTP in <span className="font-semibold text-green-600">{emailCountdown}s</span></p>
              ) : (
                <button onClick={async () => { setError(''); await sendEmailOTP(); }} disabled={loading} className="text-sm text-green-600 font-semibold hover:underline disabled:opacity-50" data-testid="signup-resend-email-otp">Resend Email OTP</button>
              )}
            </div>
            <div className="text-center">
              <button onClick={() => { setStep('form'); setEmailOtp(''); setError(''); }} className="text-sm text-gray-500 hover:underline">Back to form</button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // ── Phone OTP Verification Screen ──
  if (step === 'otp') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center px-3 py-6 sm:p-4 sm:py-12">
        <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
        <Card className="w-full max-w-md p-6 sm:p-8 shadow-2xl mx-1 sm:mx-0" data-testid="signup-otp-screen">
          <div className="flex justify-center mb-5">
            <img src="/logo.jpeg" alt="Logo" className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl shadow-lg object-cover" />
          </div>
          <div className="text-center mb-6">
            <Shield className="w-10 h-10 text-green-600 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-gray-900 mb-1">Verify Your Phone</h2>
            <p className="text-sm text-gray-500">An OTP has been sent to <strong>+91 {getCleanPhone(formData.phone)}</strong></p>
            <button onClick={() => { setStep('form'); setOtp(''); setError(''); }} className="text-xs text-green-600 hover:underline mt-1" data-testid="change-phone-btn">Change number</button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="signup-otp-error">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Enter OTP</label>
              <input ref={otpInputRef} type="text" value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleVerifyOTP(); } }}
                placeholder="● ● ● ●" maxLength={6}
                className="w-full px-4 py-3 text-center text-2xl tracking-[0.5em] font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                data-testid="signup-otp-input" />
            </div>

            <Button onClick={handleVerifyOTP} disabled={loading || otp.length < 4}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-3 text-base font-semibold"
              data-testid="signup-verify-otp-btn">
              {loading ? 'Creating Account...' : 'Verify & Sign Up'}
            </Button>

            <div className="text-center">
              {countdown > 0 ? (
                <p className="text-sm text-gray-500">Resend OTP in <span className="font-semibold text-green-600">{countdown}s</span></p>
              ) : (
                <button onClick={handleResendOTP} disabled={loading} className="text-sm text-green-600 font-semibold hover:underline disabled:opacity-50" data-testid="signup-resend-otp">Resend OTP</button>
              )}
            </div>
            <div className="text-center">
              <button onClick={() => { setStep('form'); setOtp(''); setError(''); }} className="text-sm text-gray-500 hover:underline">Back to form</button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // ── Registration Form ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center px-3 py-6 sm:p-4 sm:py-12">
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>

      <Card className="w-full max-w-2xl p-4 sm:p-6 md:p-8 shadow-2xl mx-1 sm:mx-0">
        <div className="flex justify-center mb-4 sm:mb-6">
          <img src="/logo.jpeg" alt="Logo" className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl shadow-lg object-cover" />
        </div>
        <div className="text-center mb-5 sm:mb-8">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent mb-1 sm:mb-2">{t('createAccount')}</h1>
          <p className="text-gray-600 text-xs sm:text-sm md:text-base">{t('signupTagline')}</p>
        </div>

        {error && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="signup-form-error">
            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm text-red-600">
              <span>{error}</span>
              {duplicatePhone && (
                <span> Please <Link to="/login" className="font-semibold underline text-green-700 hover:text-green-800" data-testid="duplicate-login-link">Login here</Link>.</span>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleFormSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">
          {/* Name */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('fullName')} *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
              <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder={t('fullNamePlaceholder')}
                className="w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition" data-testid="signup-name-input" />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('phoneNumber')} *</label>
            <IndianPhoneInput
              value={formData.phone}
              onChange={(val) => { setFormData({ ...formData, phone: val || '' }); setError(''); setDuplicatePhone(false); }}
              testId="signup-phone-input"
              disabled={loading}
            />
            <p className="text-[10px] sm:text-xs text-gray-400 mt-1">An OTP will be sent to this number for verification</p>
          </div>

          {/* Password & Confirm */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('password')} *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                <input type="password" name="password" value={formData.password} onChange={handleChange} placeholder={t('passwordPlaceholder')}
                  className="w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition" data-testid="signup-password-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('confirmPassword')} *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} placeholder={t('confirmPasswordPlaceholder')}
                  className="w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition" data-testid="signup-confirm-password-input" />
              </div>
            </div>
          </div>

          {/* Email (Required) */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter your email address" required
                className="w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition" data-testid="signup-email-input" />
            </div>
            <p className="text-[10px] sm:text-xs text-gray-400 mt-1">A verification OTP will be sent to this email during signup.</p>
          </div>

          {/* State & District */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('state')} *</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 z-10 pointer-events-none" />
                <select name="state" value={formData.state} onChange={handleChange}
                  className="w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition appearance-none bg-white" data-testid="signup-state-select">
                  <option value="">{t('selectState')}</option>
                  {Object.keys(DISTRICTS).map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('district')} *</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 z-10 pointer-events-none" />
                <select name="district" value={formData.district} onChange={handleChange} disabled={!formData.state}
                  className="w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition appearance-none bg-white disabled:bg-slate-100 disabled:cursor-not-allowed" data-testid="signup-district-select">
                  <option value="">{t('selectDistrict')}</option>
                  {formData.state && DISTRICTS[formData.state].map((d) => (<option key={d} value={d}>{d}</option>))}
                </select>
              </div>
            </div>
          </div>

          {/* Age & Caste */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('age')} *</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 z-10 pointer-events-none" />
                <select name="age" value={formData.age} onChange={handleChange}
                  className="w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition appearance-none bg-white" data-testid="signup-age-select">
                  <option value="">Select age range</option>
                  <option value="18-24">18 – 24</option><option value="25-34">25 – 34</option><option value="35-44">35 – 44</option>
                  <option value="45-54">45 – 54</option><option value="55-64">55 – 64</option><option value="65+">65+</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('casteCategory')} *</label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 z-10 pointer-events-none" />
                <select name="caste" value={formData.caste} onChange={handleChange}
                  className="w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition appearance-none bg-white" data-testid="signup-caste-select">
                  <option value="">{t('selectCategory')}</option>
                  {CASTE_CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>
            </div>
          </div>

          {/* Occupation */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('occupation') || 'Occupation'} *</label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 z-10 pointer-events-none" />
              <select name="occupation" value={formData.occupation} onChange={handleChange}
                className="w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition appearance-none bg-white" data-testid="signup-occupation-select">
                <option value="">{t('selectOccupation') || 'Select Occupation'}</option>
                {OCCUPATION_OPTIONS.map((o) => (<option key={o.key} value={o.key}>{t(o.translationKey) || o.key}</option>))}
              </select>
            </div>
          </div>

          {/* Household Income */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('householdIncome')} *</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 z-10 pointer-events-none" />
              <select name="household_income" value={formData.household_income} onChange={handleChange}
                className="w-full pl-9 sm:pl-10 pr-3 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition appearance-none bg-white" data-testid="signup-income-select">
                <option value="">{t('selectIncome')}</option>
                {INCOME_RANGES.map((r) => (<option key={r} value={r}>{r}</option>))}
              </select>
            </div>
          </div>

          {/* Terms */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-start gap-3">
              <input type="checkbox" id="terms-checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer" data-testid="terms-checkbox" />
              <label htmlFor="terms-checkbox" className="text-sm text-gray-700 cursor-pointer">
                {t('termsAgreementLabel') || 'I have read and agree to the'}{' '}
                <button type="button" onClick={() => setShowTermsModal(true)} className="text-green-600 font-semibold hover:text-green-700 hover:underline transition" data-testid="view-terms-btn">
                  {t('termsConditionsPrivacy') || 'Terms, Conditions, and Privacy Policy'}
                </button>{' *'}
              </label>
            </div>
            {termsAccepted && (
              <div className="mt-2 flex items-center gap-2 text-green-600 text-xs">
                <CheckCircle className="w-4 h-4" /><span>{t('termsAcceptedConfirm') || 'You have agreed to the terms'}</span>
              </div>
            )}
          </div>

          {/* Submit */}
          <Button type="submit" disabled={loading || !termsAccepted}
            className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-2.5 sm:py-3 text-sm sm:text-base md:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="signup-submit-btn">
            {loading ? 'Sending OTP...' : 'Get OTP & Register'}
          </Button>

          {/* Caution */}
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-amber-800 text-xs sm:text-sm text-center flex items-center justify-center gap-2">
              <span className="text-amber-600">⚠️</span>
              <span>Please provide accurate personal and income details, as incorrect information may affect your eligibility for schemes. Name, phone number, and category cannot be changed after signup.</span>
            </p>
          </div>
        </form>

        {/* Login Link */}
        <div className="mt-4 sm:mt-6 text-center">
          <p className="text-gray-600 text-xs sm:text-sm md:text-base">
            {t('alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-green-600 font-semibold hover:text-green-700 transition">{t('login')}</Link>
          </p>
        </div>
      </Card>

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowTermsModal(false); }}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" data-testid="terms-modal">
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 bg-gradient-to-r from-green-600 to-green-700">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-white" />
                <h2 className="text-lg sm:text-xl font-bold text-white">{t('termsModalTitle') || 'Terms, Conditions & Privacy Policy'}</h2>
              </div>
              <button onClick={() => setShowTermsModal(false)} className="p-2 hover:bg-white/20 rounded-full transition" data-testid="close-terms-modal-btn">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 sm:p-8">
              <div className="prose prose-sm max-w-none text-gray-700" ref={termsRef}>
                <h3 className="text-green-700 font-bold text-lg mb-2 text-center">KnowMySchemes (KMS) Terms & Conditions and Privacy Policy</h3>
                <p className="text-xs text-gray-500 text-center mb-5">Last Updated: April 2026 | Effective Date: 17 April 2026</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5">
                  <p className="text-sm font-semibold text-red-700 leading-relaxed">IMPORTANT DISCLAIMER: KnowMySchemes is a PRIVATE service provider. We are NOT a government entity, government portal, or affiliated with any government department. We do NOT guarantee approval of any scheme or application. Approval is solely at the discretion of the concerned government authority.</p>
                </div>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">1. Introduction & Acceptance</h4>
                <p className="text-sm mb-4 leading-relaxed">KMS (KnowMySchemes) is a digital platform that provides information, assistance, and support services to help citizens understand eligibility, prepare applications, upload documents, and access public and welfare-related government schemes. By accessing or using the KMS website or mobile application, you agree to be bound by these Terms, Conditions, and Privacy Policy. If you do not agree, you must discontinue use of the platform immediately.</p>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">2. Eligibility to Use</h4>
                <ul className="list-disc pl-5 text-sm mb-4 space-y-1">
                  <li>Users must be at least 18 years of age or have valid legal guardian consent.</li>
                  <li>All information provided during registration must be accurate, complete, and up to date.</li>
                  <li>Providing false, misleading, or incomplete information may result in suspension or termination of the account without notice.</li>
                  <li>Users are responsible for maintaining the confidentiality of their login credentials and all activities conducted through their account.</li>
                </ul>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">3. Services Offered</h4>
                <p className="text-sm mb-2">KMS offers the following services for facilitation purposes only:</p>
                <ul className="list-disc pl-5 text-sm mb-2 space-y-1">
                  <li>Eligibility checking for government schemes</li>
                  <li>Application assistance and form filling</li>
                  <li>Document upload and verification support</li>
                  <li>Information delivery and status tracking</li>
                  <li>Notifications and alerts for relevant schemes</li>
                  <li>Customer support and guided assistance</li>
                  <li>AI-powered impact analysis (KMS Smart) — personalised policy impact reports based on user profile</li>
                  <li>Income intelligence recommendations based on user occupation and location</li>
                  <li>Government job listings aggregated from official government recruitment portals</li>
                </ul>
                <p className="text-sm mb-4 leading-relaxed font-medium">KMS charges fees solely for the above assistance services. KMS does NOT sell government benefits, influence government decisions, or assure approvals. Payment for services does not guarantee acceptance or receipt of any government benefit.</p>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">4. Government Jobs Listing</h4>
                <p className="text-sm mb-2 leading-relaxed">KnowMySchemes provides a Government Jobs listing section for informational purposes only. This feature aggregates publicly available job notifications from official government recruitment websites to help citizens stay informed about employment opportunities across India.</p>
                <p className="text-sm mb-2 font-medium">4.1 Nature of Government Jobs Listings</p>
                <ul className="list-disc pl-5 text-sm mb-2 space-y-1">
                  <li>All government job notifications are sourced from publicly available official government recruitment websites including but not limited to UPSC, SSC, Railway Recruitment Boards, APPSC, TSPSC, KPSC, IBPS, and other central and state government portals.</li>
                  <li>KnowMySchemes is NOT a government recruitment portal and is NOT affiliated with any government recruitment body or authority.</li>
                  <li>Job listings are aggregated and displayed for informational convenience only.</li>
                  <li>Users must verify all job details on the respective official government website before applying.</li>
                  <li>All job application links redirect users to official government websites. KnowMySchemes does not process, collect, or handle any job applications or application fees.</li>
                  <li>KnowMySchemes displays job notifications from central government bodies, state governments of Andhra Pradesh, Telangana, Karnataka, and other states across India.</li>
                  <li>Job categories covered include Administrative and Civil Services, Banking and Finance, Railway, Teaching and Education, Defence and Paramilitary, Police and Law, Technology and IT, Medical and Health, Engineering and Technical, Clerical and Support, Agriculture, Post Office, Science and Research, Forest and Environment, Social Welfare, and Sports Quota.</li>
                </ul>
                <p className="text-sm mb-2 font-medium">4.2 Accuracy and Disclaimer</p>
                <ul className="list-disc pl-5 text-sm mb-2 space-y-1">
                  <li>We do not guarantee the completeness, accuracy, or timeliness of the information displayed.</li>
                  <li>Job notifications may be updated, modified, or cancelled by the respective government authority at any time without prior notice.</li>
                  <li>KnowMySchemes shall NOT be liable for any loss, missed opportunity, or damage arising from reliance on job listings.</li>
                  <li>KnowMySchemes does not charge any fee for viewing government job listings. Any entity claiming to offer government job assistance on behalf of KnowMySchemes for a fee is fraudulent.</li>
                  <li>Job listing data is refreshed periodically every 24 hours. KnowMySchemes does not guarantee real-time accuracy of vacancy counts, deadlines, or eligibility criteria.</li>
                </ul>
                <p className="text-sm mb-2 font-medium">4.3 Data Sources</p>
                <p className="text-sm mb-2 leading-relaxed">Government job data is sourced from publicly available official government recruitment portals including UPSC (upsc.gov.in), SSC (ssc.gov.in), Railway Recruitment Boards (indianrailways.gov.in), APPSC (psc.ap.gov.in), TSPSC (tspsc.gov.in), KPSC (kpsc.kar.nic.in), IBPS (ibps.in), Employment News (employmentnews.gov.in), NCS Portal (ncs.gov.in), and other official state and central government recruitment websites. All data displayed is publicly available information intended for general citizen awareness. KnowMySchemes does not reproduce complete government notifications or PDFs. We display summary information and link directly to original official sources for full details.</p>
                <p className="text-sm mb-2 font-medium">4.4 User Responsibilities for Government Jobs</p>
                <ul className="list-disc pl-5 text-sm mb-4 space-y-1">
                  <li>Users must independently verify all job details including eligibility, qualifications, age limits, reservation criteria, and application fees on the official government website.</li>
                  <li>Users are solely responsible for submitting applications correctly and on time through official government channels.</li>
                  <li>KnowMySchemes bears no responsibility for missed deadlines or incorrect applications.</li>
                  <li>Users should not pay any fees to third parties for government job applications. All legitimate government job applications are processed through official government portals only.</li>
                </ul>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">5. OTP Verification & Account Authentication</h4>
                <p className="text-sm mb-2 font-medium">5.1 How OTP Works</p>
                <ul className="list-disc pl-5 text-sm mb-2 space-y-1">
                  <li>When you register or login, a 4-digit OTP will be sent to your registered mobile number via SMS.</li>
                  <li>The OTP is valid for 5 minutes only. After expiry a new OTP must be requested.</li>
                  <li>OTPs are sent through our registered SMS infrastructure as per TRAI DLT regulations.</li>
                  <li>Standard SMS charges may apply as per your mobile operator plan.</li>
                </ul>
                <p className="text-sm mb-2 font-medium">5.2 User Responsibilities</p>
                <ul className="list-disc pl-5 text-sm mb-2 space-y-1">
                  <li>Never share your OTP with anyone including KnowMySchemes support staff.</li>
                  <li>KnowMySchemes will NEVER ask for your OTP over call, email, or chat.</li>
                  <li>If you receive an OTP without initiating a login, change your password immediately and contact support.</li>
                  <li>Multiple failed OTP attempts may result in temporary account lockout for 30 minutes for security purposes.</li>
                </ul>
                <p className="text-sm mb-2 font-medium">5.3 OTP Data</p>
                <ul className="list-disc pl-5 text-sm mb-4 space-y-1">
                  <li>OTPs are not stored after verification is complete.</li>
                  <li>Mobile numbers used for OTP are stored securely as part of your account credentials.</li>
                  <li>OTP logs are maintained for fraud prevention and security audit purposes only.</li>
                </ul>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">6. Subscription Plans & Payment</h4>
                <ul className="list-disc pl-5 text-sm mb-2 space-y-1">
                  <li>Free users may access limited content, basic eligibility information, and a limited number of KMS Smart analyses.</li>
                  <li>Premium users receive unlimited KMS Smart analyses, priority support, and full assisted services.</li>
                  <li>Current subscription plan: Rs. 1,499 for 3 years (subject to change with prior notice).</li>
                  <li>Subscriptions are personal, non-transferable, and time-bound.</li>
                  <li>Payments are processed securely through Razorpay payment gateway for website and Android users.</li>
                  <li>KMS does not store card or banking information.</li>
                  <li>Subscription period begins from the date of successful payment and expires after 3 years (1,095 days).</li>
                </ul>
                <p className="text-sm mb-2 font-medium">6.1 Apple In-App Purchase (iOS Users)</p>
                <ul className="list-disc pl-5 text-sm mb-2 space-y-1">
                  <li>iOS users may purchase the KnowMySchemes Pro subscription through Apple In-App Purchase (IAP) as an alternative payment method.</li>
                  <li>Apple In-App Purchase is processed and managed entirely by Apple Inc. through the App Store payment system.</li>
                  <li>The Pro subscription is available as a one-time non-consumable purchase through Apple IAP at the equivalent local currency price set by Apple for your region.</li>
                  <li>Apple In-App Purchase is subject to Apple's Terms and Conditions and Privacy Policy available at apple.com/legal/internet-services/itunes.</li>
                  <li>Payments made through Apple In-App Purchase are processed by Apple and are subject to Apple's refund policies. KnowMySchemes has no control over Apple's payment processing or refund decisions.</li>
                  <li>If you purchase through Apple IAP your subscription will be activated on your KnowMySchemes account immediately upon successful payment confirmation from Apple.</li>
                  <li>Apple takes a commission on In-App Purchase transactions as per their standard App Store guidelines. This does not affect the features or duration of your KnowMySchemes Pro subscription.</li>
                  <li>To restore a previously purchased subscription on a new iOS device use the Restore Purchases option available in the app settings.</li>
                  <li>For refund requests related to Apple In-App Purchase please contact Apple Support directly at reportaproblem.apple.com as KnowMySchemes cannot process refunds for Apple IAP transactions.</li>
                </ul>
                <p className="text-sm mb-2 font-medium">6.2 Payment Methods Summary</p>
                <ul className="list-disc pl-5 text-sm mb-4 space-y-1">
                  <li>Website — Razorpay payment gateway</li>
                  <li>Android app — Razorpay payment gateway</li>
                  <li>iOS app — Apple In-App Purchase (IAP) or visit knowmyschemes.com to pay via Razorpay</li>
                </ul>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">7. Refund Policy</h4>
                <ul className="list-disc pl-5 text-sm mb-4 space-y-1">
                  <li>Service fees are NON-REFUNDABLE once application assistance has commenced.</li>
                  <li>If KMS fails to deliver the agreed service due to a fault on our end, a full refund will be issued within 7 working days.</li>
                  <li>Subscription cancellation mid-term will not attract a refund for the remaining period.</li>
                  <li>Refund requests must be raised within 48 hours of payment through official support channels.</li>
                  <li>Refunds, if applicable, will be credited to the original payment method within 7-10 working days.</li>
                  <li>No refund will be issued for government rejection of applications as this is beyond KMS control.</li>
                  <li>For subscriptions purchased through Apple In-App Purchase all refund requests must be directed to Apple Inc. KnowMySchemes does not have the ability to process refunds for Apple IAP transactions.</li>
                </ul>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">8. AI-Powered Features & Training Data</h4>
                <p className="text-sm mb-2 font-medium">8.1 AI Services Used</p>
                <ul className="list-disc pl-5 text-sm mb-2 space-y-1">
                  <li>Our proprietary AI technology — for generating personalised policy impact analysis and scheme recommendations.</li>
                  <li>Trusted third-party news sources — for fetching latest government policy news and budget updates.</li>
                  <li>All AI responses are informational only and should not be treated as professional legal or financial advice.</li>
                </ul>
                <p className="text-sm mb-2 font-medium">8.2 Anonymous Training Data Collection</p>
                <ul className="list-disc pl-5 text-sm mb-4 space-y-1">
                  <li>With your consent, KnowMySchemes may collect anonymous usage data from KMS Smart analyses to improve our AI systems.</li>
                  <li>Data collected includes: occupation type, location (district/state), policy event selected, language preference, AI response quality metrics, benefit scores, and anonymised action step data.</li>
                  <li>We do NOT collect or store your name, phone number, email, or any personally identifiable information for training purposes.</li>
                  <li>Only anonymised, aggregated data is used for AI improvement.</li>
                  <li>You may opt out of training data collection at any time through your profile settings.</li>
                  <li>Opting out does not affect your access to any KnowMySchemes features.</li>
                </ul>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">9. Data Collection & Privacy</h4>
                <p className="text-sm mb-2 font-medium">9.1 Information We Collect</p>
                <ul className="list-disc pl-5 text-sm mb-2 space-y-1">
                  <li>Personal information: name, contact details, address, date of birth, demographic data, income information, and category details.</li>
                  <li>Occupation information: your profession, business type, or educational background for personalised scheme matching.</li>
                  <li>Documents: identification proofs and certificates voluntarily uploaded for service facilitation.</li>
                  <li>Technical data: IP address, device data, app usage logs, platform type (website/iOS/Android), and login history.</li>
                  <li>Payment data: transaction references and subscription status for accounting and verification.</li>
                  <li>OTP logs: kept for security and fraud prevention purposes only.</li>
                </ul>
                <p className="text-sm mb-2 font-medium">9.2 How We Use Your Information</p>
                <ul className="list-disc pl-5 text-sm mb-2 space-y-1">
                  <li>To deliver services, assess eligibility, and verify documents.</li>
                  <li>To power KMS Smart AI analysis with personalised scheme recommendations.</li>
                  <li>To manage accounts, process subscriptions, and send notifications.</li>
                  <li>To provide customer support and improve platform functionality.</li>
                  <li>To comply with legal requirements and prevent fraud.</li>
                </ul>
                <p className="text-sm mb-2 font-medium">9.3 Data Sharing</p>
                <p className="text-sm mb-4">User information is NOT sold or rented. Information may be shared with government portals (with consent), payment processors (Razorpay) for website and Android, Apple Inc. for In-App Purchase processing on iOS devices, SMS service providers for OTP delivery only, AI service providers for analysis generation (no personally identifiable data is shared), technology service providers for platform operations, and regulatory authorities where legally required.</p>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">10. Data Retention & Security</h4>
                <ul className="list-disc pl-5 text-sm mb-4 space-y-1">
                  <li>Account data is retained while the account is active.</li>
                  <li>Documents are stored as required for service delivery and legal compliance.</li>
                  <li>OTP logs are retained for 90 days for security audit purposes then permanently deleted.</li>
                  <li>Anonymous training data may be retained indefinitely in aggregated form.</li>
                  <li>After retention periods expire, data is securely deleted or anonymized.</li>
                  <li>Security measures include encryption, access controls, secure servers, and monitoring systems.</li>
                  <li>No system can guarantee complete security. Users access the platform at their own risk.</li>
                </ul>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">11. User Rights</h4>
                <ul className="list-disc pl-5 text-sm mb-4 space-y-1">
                  <li>Access, correct, update, or delete personal data.</li>
                  <li>Request restriction of data processing.</li>
                  <li>Obtain copies of data in a portable format.</li>
                  <li>Object to certain processing activities.</li>
                  <li>Withdraw consent for anonymous training data collection at any time.</li>
                  <li>Opt out of SMS communications (except critical OTP messages required for login).</li>
                </ul>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">12. Cookies & Tracking</h4>
                <p className="text-sm mb-4">KMS may use cookies and similar technologies to maintain sessions, enhance usability, and analyze platform performance. Users can manage cookie preferences through their device or browser settings.</p>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">13. Prohibited Activities</h4>
                <ul className="list-disc pl-5 text-sm mb-4 space-y-1">
                  <li>Fraudulent activity or misrepresentation of identity.</li>
                  <li>Unauthorized access to other user accounts.</li>
                  <li>Copying, reproducing, or distributing platform content without permission.</li>
                  <li>Account sharing between multiple individuals.</li>
                  <li>Attempting to bypass OTP verification or authentication systems.</li>
                  <li>Using automated bots or scripts to access KMS Smart or any platform features.</li>
                  <li>Any activity that violates Indian law including the Information Technology Act 2000.</li>
                </ul>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">14. Limitation of Liability</h4>
                <p className="text-sm mb-2">KMS shall NOT be liable for:</p>
                <ul className="list-disc pl-5 text-sm mb-4 space-y-1">
                  <li>Government rejection of scheme applications.</li>
                  <li>Processing delays by government departments.</li>
                  <li>Technical failures or data loss caused by external factors.</li>
                  <li>Losses arising from incorrect information provided by users.</li>
                  <li>AI analysis results — KMS Smart provides informational analysis only, not professional advice.</li>
                  <li>SMS delivery failures due to network issues beyond our control.</li>
                  <li>Inaccuracies or outdated information in government job listings. Users must verify all job details on official government websites.</li>
                  <li>Missed application deadlines arising from reliance on job listings displayed on KnowMySchemes.</li>
                  <li>Any issues arising from Apple In-App Purchase transactions including payment failures, refund decisions, or App Store availability.</li>
                </ul>
                <p className="text-sm mb-4 italic text-gray-500">All services are provided on an "as-is" basis. KMS reserves the right to suspend or discontinue services for maintenance, upgrades, or policy enforcement without prior notice.</p>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">15. Dispute Resolution</h4>
                <ul className="list-disc pl-5 text-sm mb-4 space-y-1">
                  <li>In case of any dispute, parties shall first attempt resolution through mutual discussion within 15 days.</li>
                  <li>If unresolved, disputes shall be referred to arbitration under the Arbitration and Conciliation Act, 1996.</li>
                  <li>These terms are governed by the laws of India. Jurisdiction: Courts of Guntur, Andhra Pradesh, India.</li>
                  <li>Disputes related to Apple In-App Purchase transactions are governed by Apple's terms and conditions and must be resolved directly with Apple Inc.</li>
                </ul>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">16. Grievance Officer</h4>
                <p className="text-sm mb-4">Email: info@knowmyschemes.com | Response Time: Within 30 days of receiving the complaint</p>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">17. Changes to Terms</h4>
                <p className="text-sm mb-4">KMS reserves the right to modify these Terms, Conditions, and Privacy Policy at any time. Updated versions will be published on the website and application. Continued use of the platform after updates constitutes acceptance of the revised terms.</p>

                <h4 className="text-green-700 font-bold text-sm mt-4 mb-2">18. Contact Us</h4>
                <p className="text-sm mb-2">Website: www.knowmyschemes.com | Email: support@knowmyschemes.com | Grievance: info@knowmyschemes.com</p>
                <p className="text-sm mb-4">For Apple In-App Purchase issues: reportaproblem.apple.com</p>

                <div className="mt-6 p-4 bg-green-50 border border-green-100 rounded-lg">
                  <p className="text-sm text-green-800 font-medium leading-relaxed">By using KnowMySchemes, you acknowledge that you have read, understood, and agreed to these Terms, Conditions, and Privacy Policy and consent to the collection, processing, and use of your personal data as described herein.</p>
                  <p className="text-xs text-green-700 mt-2 italic text-center">KnowMySchemes — Helping Citizens Access What They Deserve</p>
                  <p className="text-xs text-gray-500 mt-1 text-center">www.knowmyschemes.com | info@knowmyschemes.com</p>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row gap-3">
              <Button onClick={() => { setTermsAccepted(true); setShowTermsModal(false); }}
                className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white" data-testid="accept-terms-btn">
                <CheckCircle className="w-4 h-4 mr-2" />{t('acceptTerms') || 'I Accept the Terms'}
              </Button>
              <Button variant="outline" onClick={() => setShowTermsModal(false)} className="flex-1 border-gray-300">{t('close') || 'Close'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Signup;
